/**
 * Shared title and description builders for public SEO route families.
 *
 * Server render-data functions and hydrated React pages must consume these
 * exact values so canonical URLs do not change metadata after hydration.
 */

export interface PageSeoMetadata {
  title: string;
  description: string;
}

const BRANDED_TITLE_SUFFIX = " | Premier Pharma";
const MAX_BRANDED_TITLE_LENGTH = 65;

export const HOME_META_DESCRIPTION =
  "Premier Pharma supplies wholesale pharmaceuticals to licensed pharmacies, clinics, medical practices, and veterinary businesses, with free account setup.";

export const SOFTWARE_META_DESCRIPTION =
  "ScriptTrack includes starter compliance software with a free account. Optional paid products support inventory, dispensing, prescribing, and billing workflows.";

export const DSCSA_HUB_META_DESCRIPTION =
  "Explore DSCSA compliance resources for pharmacies, clinics, and distributors, including timelines, serialization, T2 documentation, and a free checklist.";

function clampTitleBase(base: string, maxBaseLength: number): string {
  if (base.length <= maxBaseLength) return base;
  const cut = base.slice(0, maxBaseLength);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 15 ? cut.slice(0, lastSpace) : cut)
    .replace(/[\s\-\u2014\u2013]+$/, "")
    .trimEnd();
}

function clampBrandedTitleBase(base: string): string {
  const maxBaseLength = MAX_BRANDED_TITLE_LENGTH - BRANDED_TITLE_SUFFIX.length;
  if (base.length <= maxBaseLength) return base;
  return `${base.slice(0, maxBaseLength - 1).trimEnd()}…`;
}

export function buildCategoryPageSeo(name: string, total: number): PageSeoMetadata {
  const lowerName = name.toLowerCase();
  return {
    title: clampTitleBase(`Wholesale ${name} — Pharmacy Supply`, 55),
    description: `Wholesale ${lowerName} from Premier Pharma: ${total.toLocaleString()}+ products with transparent pricing, NABP accreditation, and DSCSA-compliant same-day shipping for pharmacies.`,
  };
}

export function buildManufacturerPageSeo(name: string, total: number): PageSeoMetadata {
  return {
    title: clampTitleBase(`${name} Wholesale — ${total.toLocaleString()} SKUs`, 55),
    description: `Licensed wholesale distributor of ${name} pharmaceuticals. ${total.toLocaleString()} SKUs, DSCSA-compliant, same-day shipping for licensed pharmacies and clinics.`,
  };
}

export function buildDrugIndexPageSeo(total: number): PageSeoMetadata {
  return {
    title: "Generic Drug Wholesale Directory",
    description: `Browse ${total}+ generic drugs for wholesale at Premier Pharma. Pharmacies, clinics & veterinary practices — view pricing and order same-day. DSCSA-compliant.`,
  };
}

export interface DrugDetailPageSeoInput {
  displayName: string;
  primaryBrand?: string | null;
  firstStrength?: string | null;
  dosageForm?: string | null;
  productCount: number;
}

export function buildDrugDetailPageSeo(input: DrugDetailPageSeoInput): PageSeoMetadata {
  const brandSuffix = input.primaryBrand ? ` (generic ${input.primaryBrand})` : "";
  const strengthSuffix = input.firstStrength ? ` ${input.firstStrength}` : "";
  const formSuffix = input.dosageForm ? ` ${input.dosageForm}` : "";
  const title = clampBrandedTitleBase(
    `${input.displayName}${brandSuffix}${strengthSuffix}${formSuffix} Wholesale`,
  );
  const description = input.primaryBrand
    ? `Buy wholesale ${input.displayName} (${input.primaryBrand} generic) from Premier Pharma — ${input.productCount} SKUs in${formSuffix || " various forms"}. DSCSA-compliant, same-day shipping for licensed pharmacies.`
    : `Buy wholesale ${input.displayName} from Premier Pharma — ${input.productCount} SKUs in${formSuffix || " various forms"}. DSCSA-compliant, same-day shipping for licensed pharmacies and clinics.`;

  return { title, description };
}

export function buildTherapeuticClassIndexPageSeo(total: number): PageSeoMetadata {
  return {
    title: "Therapeutic Drug Class Directory",
    description: `Browse ${total} therapeutic drug classes at Premier Pharma — generics for pharmacies, clinics & veterinary practices. Same-day pricing, DSCSA-compliant.`,
  };
}

export interface TherapeuticClassDetailPageSeoInput {
  className: string;
  drugCount: number;
  productCount: number;
  inStockCount: number;
}

export function buildTherapeuticClassDetailPageSeo(
  input: TherapeuticClassDetailPageSeoInput,
): PageSeoMetadata {
  return {
    title: `${input.className} Wholesale Drugs — ${input.drugCount} Generics Available`,
    description: `Shop ${input.className} wholesale: ${input.drugCount} generic drugs and ${input.productCount} SKUs${input.inStockCount > 0 ? `, including ${input.inStockCount} in stock` : ""}. DSCSA-compliant, same-day shipping for licensed healthcare providers.`,
  };
}

export function buildNdcIndexPageSeo(): PageSeoMetadata {
  return {
    title: "NDC Lookup — Drug Identification & Wholesale",
    description: "Look up any NDC (National Drug Code) to identify pharmaceutical products, check wholesale availability, and find DSCSA transaction data at Premier Pharma.",
  };
}

export interface NdcDetailPageSeoInput {
  ndc: string;
  displayName: string;
  strength?: string | null;
  dosageForm?: string | null;
  manufacturer?: string | null;
  inStock?: boolean | null;
}

export function buildNdcDetailPageSeo(input: NdcDetailPageSeoInput): PageSeoMetadata {
  const strengthSuffix = input.strength ? ` ${input.strength}` : "";
  const formSuffix = input.dosageForm ? ` ${input.dosageForm}` : "";
  return {
    title: clampBrandedTitleBase(
      `NDC ${input.ndc}: ${input.displayName}${strengthSuffix} — Wholesale Lookup`,
    ),
    description: `NDC ${input.ndc}: ${input.displayName}${strengthSuffix}${formSuffix}${input.manufacturer ? ` by ${input.manufacturer}` : ""}. ${input.inStock ? "In stock" : "Available"} at Premier Pharma. DSCSA-compliant wholesale for licensed pharmacies and clinics.`,
  };
}